import React from "react";

// polar point on a 180° gauge (pct 0 = left, 100 = right)
const polar = (cx, cy, r, pct) => {
  const a = Math.PI * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
};
const arc = (cx, cy, r, p0, p1) => {
  const [x0, y0] = polar(cx, cy, r, p0);
  const [x1, y1] = polar(cx, cy, r, p1);
  return `M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`;
};

const colorFor = (val, base) =>
  val >= base ? "#059669" : val >= base - 10 ? "#d97706" : "#dc2626";

function Gauge({ value, baseline }) {
  const cx = 130, cy = 132, r = 100;
  const c = colorFor(value, baseline);
  const [nx, ny] = polar(cx, cy, r - 22, value);
  const [bx, by] = polar(cx, cy, r + 9, baseline);
  const [bx2, by2] = polar(cx, cy, r - 16, baseline);
  return (
    <svg viewBox="0 0 260 156" className="w-full max-w-[280px]" data-testid="oee-gauge-svg">
      {/* zone track */}
      <path d={arc(cx, cy, r, 0, 50)} stroke="#fecaca" strokeWidth="14" fill="none" strokeLinecap="round" />
      <path d={arc(cx, cy, r, 50, 75)} stroke="#fde68a" strokeWidth="14" fill="none" />
      <path d={arc(cx, cy, r, 75, 100)} stroke="#bbf7d0" strokeWidth="14" fill="none" strokeLinecap="round" />
      {/* baseline / target marker */}
      <line x1={bx} y1={by} x2={bx2} y2={by2} stroke="#0f172a" strokeWidth="2.5" />
      <text x={bx} y={by - 4} textAnchor="middle" className="fill-slate-700" style={{ fontSize: 9, fontWeight: 700 }}>
        Target {baseline}%
      </text>
      {/* needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={c} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="7" fill={c} />
      {/* center value */}
      <text x={cx} y={cy - 30} textAnchor="middle" style={{ fontSize: 38, fontWeight: 800, fontFamily: "ui-monospace, monospace", fill: c }}>
        {value.toFixed(1)}
      </text>
      <text x={cx} y={cy - 13} textAnchor="middle" className="fill-slate-500" style={{ fontSize: 11, fontWeight: 600 }}>
        % OEE
      </text>
    </svg>
  );
}

function BaselineBar({ label, value, target }) {
  const c = colorFor(value, target);
  const delta = value - target;
  return (
    <div data-testid={`baseline-bar-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="font-mono font-semibold" style={{ color: c }}>
          {value.toFixed(1)}% <span className="text-slate-400 font-normal">/ {target}%</span>
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-slate-100 overflow-visible">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, value)}%`, background: c }} />
        <div className="absolute top-[-2px] h-3 w-[2px] bg-slate-900" style={{ left: `${Math.min(100, target)}%` }} title={`Target ${target}%`} />
      </div>
    </div>
  );
}

export default function ProductionEfficiency({ production }) {
  const oee = production?.oee_pct ?? 0;
  const b = production?.baselines || {};
  const base = b.oee_pct ?? 85;
  const delta = oee - base;
  const bars = [
    { label: "Availability", value: production?.availability_pct ?? 0, target: b.availability_pct ?? 90 },
    { label: "Performance", value: production?.performance_pct ?? 0, target: b.performance_pct ?? 95 },
    { label: "Quality", value: production?.quality_pct ?? 0, target: b.quality_pct ?? 99.5 },
    { label: "Yield", value: production?.yield_pct ?? 0, target: b.yield_pct ?? 98 },
  ];
  const c = colorFor(oee, base);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center" data-testid="production-efficiency">
      <div className="flex flex-col items-center lg:col-span-1">
        <Gauge value={oee} baseline={base} />
        <span
          data-testid="oee-baseline-delta"
          className="mt-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
          style={{ background: `${c}14`, color: c }}
        >
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)} pts {delta >= 0 ? "above" : "below"} target
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 lg:col-span-2">
        {bars.map((x) => <BaselineBar key={x.label} {...x} />)}
      </div>
    </div>
  );
}
